/**
 * Exécution de la synchronisation Hybride sur le poste : décide si le moteur doit tourner (licence hybride,
 * SQLite, société, consentement), le démarre et l'arrête (session, bascule de société), relaie les événements
 * du navigateur (réseau, focus, fermeture, écriture locale) et expose l'état à l'interface.
 *
 * Standalone : aucun moteur, aucun appel réseau. La couche d'interface reste à l'extérieur (injectée).
 */
import {
  HybridSyncEngine,
  type SyncNotice,
  type SyncStatus,
} from '@infra/sync/hybrid/HybridSyncEngine';
import type { SyncApiPort, SyncOutboxPort } from '@infra/sync/hybrid/SyncPorts';
import type { SyncAttentionAction, SyncAttentionItem, SyncLocalState } from '@infra/sync/hybrid/SyncBridgeTypes';
import {
  HYBRID_CONSENT_VERSION,
  evaluateHybridSyncEligibility,
  type HybridSyncEligibility,
  type HybridSyncInput,
} from './HybridSyncEligibility';
import { describeSyncStatus, type SyncLabel } from './HybridSyncLabels';
import { SYNC_LOCAL_WRITE_EVENT } from '@infra/sync/hybrid/SyncLocalWriteSignal';

export { SYNC_LOCAL_WRITE_EVENT };

export const SYNC_STATE_EVENT = 'nexxus:sync-state';
export const SYNC_REMOTE_APPLIED_EVENT = 'nexxus:sync-remote-applied';
export const SYNC_READONLY_EVENT = 'nexxus:sync-readonly';

export interface SyncAdminPort {
  enable(consentVersion: string): Promise<SyncLocalState>;
  disable(): Promise<SyncLocalState>;
  setAuthor(userId: string | null): Promise<{ ok: boolean }>;
  exportDiagnostic(): Promise<Record<string, unknown>>;
  getState(): Promise<SyncLocalState>;
}

export interface HybridSyncRuntimeDeps {
  createApi(): SyncApiPort;
  createOutbox(tenantId: string): SyncOutboxPort;
  createAdmin(tenantId: string): SyncAdminPort;
  appVersion: string;
  getDeviceLabel(): Promise<string>;
  /** Écriture locale d'une fiche (passe par la capture atomique). */
  putLocal(table: string, row: Record<string, unknown>): Promise<unknown>;
  newId(prefix: string): string;
  emit(name: string, detail: unknown): void;
  onNotice(notice: SyncNotice): void;
  /** Rôle courant renvoyé par le serveur pendant la session (mise à jour du cache d'identité). */
  onServerSession?(session: { user_id: string; role: string }): void;
  /** Abonnement aux événements du navigateur ; renvoie la fonction de désabonnement. */
  listen(event: string, handler: () => void): () => void;
  withLock?<T>(tenantId: string, fn: () => Promise<T>): Promise<T>;
}

export interface HybridSyncUiState {
  eligibility: HybridSyncEligibility | null;
  status: SyncStatus | null;
  label: SyncLabel | null;
  /** Éléments « À examiner » (entrées refusées + copies en quarantaine). */
  attentionCount: number;
  pendingCount: number;
  tenantId: string | null;
}

const COPY_TABLE: Record<string, { table: string; prefix: string }> = {
  salaries: { table: 'salaries', prefix: 'sal' },
  bulletins: { table: 'bulletins', prefix: 'bul' },
  ecritures: { table: 'ecritures', prefix: 'ecr' },
  factures: { table: 'factures', prefix: 'fa' },
  'centres-analytiques': { table: 'centres_analytiques', prefix: 'cen' },
};

export class HybridSyncRuntime {
  private engine: HybridSyncEngine | null = null;
  private tenantId: string | null = null;
  private eligibility: HybridSyncEligibility | null = null;
  private status: SyncStatus | null = null;
  private disposers: Array<() => void> = [];
  private readonly listeners = new Set<(state: HybridSyncUiState) => void>();
  private lastInput: HybridSyncInput | null = null;

  constructor(private readonly deps: HybridSyncRuntimeDeps) {}

  // --- cycle de vie -------------------------------------------------------------------------------

  /**
   * (Ré)évalue l'éligibilité et démarre ou arrête le moteur en conséquence. Appelé à l'ouverture de session,
   * à la bascule de société, au renouvellement de licence.
   */
  async activate(input: HybridSyncInput): Promise<HybridSyncEligibility> {
    this.lastInput = input;
    const verdict = evaluateHybridSyncEligibility(input);
    this.eligibility = verdict;
    if (verdict.kind !== 'eligible') {
      this.teardown();
      this.publish();
      return verdict;
    }
    const tenantId = input.sessionTenantId as string;
    if (this.engine && this.tenantId === tenantId) {
      this.publish();
      return verdict;
    }
    this.teardown();
    // Auteur des modifications enfilées = identifiant SERVEUR de l'utilisateur (comptes du serveur uniquement).
    await this.deps
      .createAdmin(tenantId)
      .setAuthor(input.sessionAccountVerified && input.sessionUserId ? input.sessionUserId : null)
      .catch(() => undefined);
    await this.startEngine(tenantId, input.sessionRole === 'admin' || input.sessionRole === 'super_admin');
    this.publish();
    return verdict;
  }

  /** Fermeture de session ou de l'application : le moteur s'arrête, la file reste intacte. */
  deactivate(): void {
    this.teardown();
    this.eligibility = null;
    this.publish();
  }

  private async startEngine(tenantId: string, _isAdmin: boolean): Promise<void> {
    void _isAdmin;
    const outbox = this.deps.createOutbox(tenantId);
    const admin = this.deps.createAdmin(tenantId);
    const engine = new HybridSyncEngine({
      tenantId,
      api: this.deps.createApi(),
      outbox,
      appVersion: this.deps.appVersion,
      deviceLabel: () => this.deps.getDeviceLabel(),
      withLock: this.deps.withLock ? <T>(fn: () => Promise<T>) => this.deps.withLock!(tenantId, fn) : undefined,
      yieldToUi: () => new Promise<void>((resolve) => setTimeout(resolve, 0)),
      onStatus: (status) => {
        this.status = status;
        this.publish();
      },
      onNotice: (notice) => this.deps.onNotice(notice),
      onServerSession: (session) => this.deps.onServerSession?.(session),
      onRemoteApplied: (resources) => this.deps.emit(SYNC_REMOTE_APPLIED_EVENT, { tenantId, resources }),
      onReadOnlyChange: (readOnly, reason) => this.deps.emit(SYNC_READONLY_EVENT, { tenantId, readOnly, reason }),
    });
    this.engine = engine;
    this.tenantId = tenantId;
    this.disposers = [
      this.deps.listen('online', () => void engine.notifyOnline('online')),
      this.deps.listen('offline', () => engine.notifyBrowserOffline()),
      this.deps.listen('focus', () => void engine.notifyOnline('focus')),
      this.deps.listen('beforeunload', () => void engine.flushOnClose()),
      this.deps.listen(SYNC_LOCAL_WRITE_EVENT, () => engine.notifyLocalWrite()),
    ];
    void admin;
    await engine.start();
  }

  private teardown(): void {
    for (const dispose of this.disposers) dispose();
    this.disposers = [];
    this.engine?.stop();
    this.engine = null;
    this.tenantId = null;
    this.status = null;
  }

  // --- consentement ----------------------------------------------------------------------------------

  /** Consentement RGPD donné par un administrateur : la capture et le moteur démarrent. */
  async acceptConsent(input: HybridSyncInput): Promise<HybridSyncEligibility> {
    const verdict = evaluateHybridSyncEligibility({ ...input, consentRecorded: false });
    if (verdict.kind !== 'consent_required' || !verdict.canConsent) return verdict;
    await this.deps.createAdmin(input.sessionTenantId as string).enable(HYBRID_CONSENT_VERSION);
    return this.activate({ ...input, consentRecorded: true });
  }

  /** Retrait du consentement : la synchronisation s'arrête, les données locales restent intactes. */
  async withdrawConsent(): Promise<{ serverNotified: boolean }> {
    const tenantId = this.tenantId;
    const input = this.lastInput;
    if (!tenantId || !input) return { serverNotified: false };
    let serverNotified = false;
    try {
      await this.deps.createApi().disable();
      serverNotified = true;
    } catch {
      serverNotified = false; // hors ligne : le retrait local s'applique quand même
    }
    this.teardown();
    await this.deps.createAdmin(tenantId).disable();
    this.eligibility = evaluateHybridSyncEligibility({ ...input, consentRecorded: false });
    this.publish();
    return { serverNotified };
  }

  /** Identifiant utilisateur SERVEUR pris comme auteur des modifications enfilées (P7). */
  async setAuthor(serverUserId: string | null): Promise<void> {
    if (!this.tenantId) return;
    await this.deps.createAdmin(this.tenantId).setAuthor(serverUserId);
  }

  // --- actions de l'interface ----------------------------------------------------------------------------

  async syncNow(): Promise<void> {
    await this.engine?.syncNow();
  }

  /** Nouvelle ouverture de session de l'utilisateur après `needs_login`. */
  async notifyLogin(): Promise<void> {
    await this.engine?.notifyLogin();
  }

  async listAttention(): Promise<SyncAttentionItem[]> {
    if (!this.tenantId) return [];
    return this.deps.createOutbox(this.tenantId).listAttention();
  }

  /**
   * Abandonner / Réessayer / Copier en nouvelle fiche. « Copier » crée une NOUVELLE fiche locale (nouvel
   * identifiant, enfilée normalement) ; pour un bulletin, un bulletin de correction non figé.
   */
  async resolveAttention(item: SyncAttentionItem, action: SyncAttentionAction): Promise<{ newId?: string }> {
    if (!this.tenantId) return {};
    const outbox = this.deps.createOutbox(this.tenantId);
    const res = await outbox.resolveAttention(item.id, action);
    let newId: string | undefined;
    if (action === 'copied' && res.payload && res.resource) {
      const target = COPY_TABLE[res.resource];
      if (target) {
        newId = this.deps.newId(target.prefix);
        const row: Record<string, unknown> = { ...res.payload, id: newId, tenant_id: this.tenantId };
        if (res.resource === 'bulletins') {
          row['statut'] = 'genere';
          delete row['date_virement'];
          delete row['reference_virement'];
        }
        await this.deps.putLocal(target.table, row);
      }
    }
    await this.refreshCounters();
    return newId ? { newId } : {};
  }

  async exportDiagnostic(): Promise<Record<string, unknown>> {
    if (!this.tenantId) return {};
    return this.deps.createAdmin(this.tenantId).exportDiagnostic();
  }

  // --- état exposé ---------------------------------------------------------------------------------------

  getUiState(): HybridSyncUiState {
    const status = this.status;
    return {
      eligibility: this.eligibility,
      status,
      label: status ? describeSyncStatus(status) : null,
      attentionCount: status?.attentionCount ?? 0,
      pendingCount: status?.pendingCount ?? 0,
      tenantId: this.tenantId,
    };
  }

  subscribe(listener: (state: HybridSyncUiState) => void): () => void {
    this.listeners.add(listener);
    listener(this.getUiState());
    return () => this.listeners.delete(listener);
  }

  private async refreshCounters(): Promise<void> {
    this.engine?.notifyLocalWrite();
  }

  private publish(): void {
    const state = this.getUiState();
    for (const listener of [...this.listeners]) {
      try {
        listener(state);
      } catch {
        /* un abonné défaillant ne bloque pas les autres */
      }
    }
    this.deps.emit(SYNC_STATE_EVENT, state);
  }
}
